import {flatten, get, isNil, isEmpty} from 'lodash'
import {isJobCardComplete, isProductionEnabled, JobBoardStatus} from 'helpers/production'
import {accountHasFeature, getProLicencesUsed, userHasFeature, userHasPermission} from 'helpers/user'
import {deliveriesSyncVersion, features, permissions, PRO_LICENCE} from 'helpers/constants'
import {isConfirmedJob, QuoteStatus} from 'helpers/quotes'
import {hasAnyDelivery, isStockableItem} from 'helpers/orders'

const TRACKING_NUMBER = '{TRACKING_NUMBER}'

export const DeliveryStatus = {
  none: 0,
  partDelivered: 1,
  fullyDelivered: 2,
}

export const getDeliveryStatus = (deliveryStatus) => {
  switch (deliveryStatus) {
    case 0:
      return 'None'
    case 1:
      return 'Part Delivered'
    case 2:
      return 'Fully Delivered'
    default:
      return 'None'
  }
}

export const DeliveryFixedMethod = {
  DHL: {
    id: 0,
    name: 'DHL',
    trackingURL: `http://www.dhl.com/en/express/tracking.html?AWB=${TRACKING_NUMBER}`,
  },
  DPD: {
    id: 1,
    name: 'DPD',
    trackingURL: `https://www.dpd.co.uk/apps/tracking/?reference=${TRACKING_NUMBER}#results`,
  },
  FEDEX: {
    id: 2,
    name: 'FedEx',
    trackingURL: `https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=${TRACKING_NUMBER}`,
  },
  PARCEL_FORCE: {
    id: 3,
    name: 'Parcel Force',
    trackingURL: `https://www.parcelforce.com/track-trace?trackNumber=${TRACKING_NUMBER}`,
  },
  ROYAL_MAIL: {
    id: 4,
    name: 'Royal Mail',
    trackingURL: `https://www3.royalmail.com/track-your-item#/tracking-results/${TRACKING_NUMBER}`,
  },
  TNT: {
    id: 5,
    name: 'TNT',
    trackingURL:
      `https://www.tnt.com/express/en_gb/site/shipping-tools/tracking.html?searchType=con&cons=${TRACKING_NUMBER}`,
  },
  UPS: {
    id: 6,
    name: 'UPS',
    trackingURL: `http://www.apps.ups.com/etracking/tracking.cgi?tracknum=${TRACKING_NUMBER}`,
  },
}

export const getTrackingUrl = (deliveryFixedMethodId, trackingNumber) => {
  const deliveryFixedMethod = Object.values(DeliveryFixedMethod)
    .find(fixedMethod => fixedMethod.id === deliveryFixedMethodId)

  return isNil(deliveryFixedMethod) || isNil(deliveryFixedMethod.trackingURL) || isEmpty(trackingNumber)
    ? null
    : deliveryFixedMethod.trackingURL.replace(TRACKING_NUMBER, trackingNumber)
}

export const DeliveryMethodType = {
  COLLECTION: 0,
  DELIVERY: 1,
}


export const DeliveryReasonType = {
  DELIVERY: 0,
  RETURN: 1,
}

export const DeliveryNoteStatus = {
  SCHEDULED: 0,
  DELIVERED: 1,
}

export const isDelivered = (status) => {
  return status === DeliveryNoteStatus.DELIVERED
}

export const isScheduled = (status) => {
  return status === DeliveryNoteStatus.SCHEDULED
}

export const getDeliveryNoteItems = (deliveryNotes) => {
  return flatten(deliveryNotes
    .map(deliveryNote => {
      const deliveryNoteItems = get(deliveryNote, 'deliveryNoteItems', [])
      return isNil(deliveryNoteItems) ? [] : deliveryNoteItems
    }))
}

export const getDeliveryMethod = (deliveryMethodId, deliveryMethods) => {
  if (isNil(deliveryMethodId))
    return null

  const deliveryMethod = deliveryMethods.find(method => method.id === deliveryMethodId)
  return isNil(deliveryMethod) ? null : deliveryMethod
}

export const getDeliveryMethodName = (deliveryMethod) => {
  return isNil(get(deliveryMethod, 'fixedMethod', null))
    ? get(deliveryMethod, 'description')
    : get(deliveryMethod, 'fixedMethod.name', null)
}

export const canReturnDeliveryNotes = (user) => {
  return (
    userHasFeature(user, features.DELIVERIES_LICENCE) &&
    accountHasFeature(user, features.RETURN_NOTES) &&
    userHasPermission(user, permissions.MANAGE_RETURN_NOTES)
  )
}

export const getDeliveryNoteStatusOptions = (user) => {
  return [
    {
      value: DeliveryNoteStatus.SCHEDULED,
      label: 'Scheduled',
    },
    {
      value: DeliveryNoteStatus.DELIVERED,
      label: 'Delivered',
    },
    {
      value: 'COLLECTED',
      label: 'Collected',
    },
    {
      value: 'AWAITING_RETURN',
      label: 'Awaiting Return',
    },
    {
      value: 'RETURNED',
      label: 'Returned',
    },
  ].filter(option => {
    if (canReturnDeliveryNotes(user))
      return true

    return option.value !== 'AWAITING_RETURN' && option.value !== 'RETURNED'
  })
}

export const getPrintedEmailedOptions = () => {
  return [
    {
      value: 'PRINTED',
      label: 'Printed',
    },
    {
      value: 'EMAILED',
      label: 'Emailed',
    },
    {
      value: 'NOT_PRINTED',
      label: 'Not Printed',
    },
    {
      value: 'NOT_EMAILED',
      label: 'Not Emailed',
    },
  ]
}

export const getDeliveryMethodOptions = (deliveryMethods) => {
  return deliveryMethods.map(deliveryMethod => {
    return {
      value: deliveryMethod.id,
      label: getDeliveryMethodName(deliveryMethod),
    }
  })
}

export const getDeliveryReturnReasonOptions = (deliveryReturnReasons) => {
  return deliveryReturnReasons.map(returnReason => {
    return {
      id: returnReason.id,
      key: returnReason.id,
      value: returnReason.id,
      label: returnReason.name,
      returnReason,
    }
  })
}

export const getReadyForDeliveryForOrderItem = (user, order, deliveryNote, orderItem, jobCards, deliveryNotes) => {
  const jobCard = (jobCards || []).find(jobCard =>
    isNil(jobCard.orderItemId) || jobCard.orderItemId === get(orderItem, 'id', null))

  const jobBoardNotInUse = !isProductionEnabled(user)
  const jobCardIsFinishedProduction = !isNil(jobCard) && isJobCardComplete(order, jobCard)
  const orderItemWasSkippedInProduction = isNil(jobCard) && get(order, 'productionScheduled')

  const jobStatus = get(order, 'jobStatus', null)
  const isNoProductionRequired = !isNil(jobStatus) && jobStatus === JobBoardStatus.noProductionRequired

  let total = get(orderItem, 'quantity', 0)
  total -= getDeliveredForOrderItem(orderItem, deliveryNotes)
  total -= getScheduledForOrderItem(orderItem, deliveryNotes)
  total += getReturnedForOrderItem(user, orderItem, deliveryNotes)

  if (!isNil(deliveryNote)) {
    const currentDeliveryNote = deliveryNotes.filter(note => note.id === get(deliveryNote, 'id', null))
    total += getScheduledForOrderItem(orderItem, currentDeliveryNote)
  }

  return (
    jobBoardNotInUse ||
    jobCardIsFinishedProduction ||
    orderItemWasSkippedInProduction ||
    isNoProductionRequired
  )
    ? Math.max(total, 0)
    : 0
}

export const getTotalQuantity = (itemsWithQuantity) =>
  itemsWithQuantity.reduce((total, item) => total + item.quantity, 0)

export const getOrderedForOrder = (order) =>
  getTotalQuantity(get(order, 'orderitems', []))

export const getNetDeliveredForOrder = (order) =>
  getTotalDeliveredForOrder(order) - getTotalReturnedForOrder(order)

export const getTotalDeliveredForOrder = (order) => {
  const deliveryNotes = get(order, 'deliveryNotes', [])
    .filter(deliveryNote => !deliveryNote.returnNote)
    .filter(deliveryNote => isDelivered(deliveryNote.status))

  return getTotalQuantity(getDeliveryNoteItems(deliveryNotes))
}

export const getTotalReturnedForOrder = (order) => {
  const deliveryNotes = get(order, 'deliveryNotes', [])
    .filter(deliveryNote => deliveryNote.returnNote)
    .filter(deliveryNote => isDelivered(deliveryNote.status))

  return getTotalQuantity(getDeliveryNoteItems(deliveryNotes))
}

export const getOrderedForOrderItem = (orderItem) => get(orderItem, 'quantity', 0)

export const getScheduledForOrderItem = (orderItem, deliveryNotes) => {
  const orderItemId = get(orderItem, 'id')

  const scheduledItems = flatten(deliveryNotes
    .filter(deliveryNote => !deliveryNote.returnNote)
    .filter(deliveryNote => deliveryNote.status === DeliveryNoteStatus.SCHEDULED)
    .map(deliveryNote => deliveryNote.deliveryNoteItems)
  ).filter(deliveryNoteItem => deliveryNoteItem.orderItemId === orderItemId)

  return getTotalQuantity(scheduledItems)
}

export const getDeliveredForOrderItem = (orderItem, deliveryNotes) => {
  const orderItemId = get(orderItem, 'id')

  const scheduledItems = flatten(deliveryNotes
    .filter(deliveryNote => !deliveryNote.returnNote)
    .filter(deliveryNote =>
      deliveryNote.status === DeliveryNoteStatus.DELIVERED
      || get(deliveryNote, 'status.id') === DeliveryNoteStatus.DELIVERED)
    .map(deliveryNote => deliveryNote.deliveryNoteItems)
  ).filter(deliveryNoteItem => deliveryNoteItem.orderItemId === orderItemId)

  return getTotalQuantity(scheduledItems)
}

export const getReturnedForOrderItem = (user, orderItem, deliveryNotes) => {
  if (!accountHasFeature(user, features.RETURN_NOTES)) return 0

  const scheduledItems = flatten(deliveryNotes
    .filter(deliveryNote => deliveryNote.returnNote)
    .filter(deliveryNote => deliveryNote.status === DeliveryNoteStatus.DELIVERED)
    .map(deliveryNote => deliveryNote.deliveryNoteItems)
  ).filter(deliveryNoteItem => deliveryNoteItem.orderItemId === orderItem.id)

  return getTotalQuantity(scheduledItems)
}

export const getOutstandingForOrderItem = (user, order, orderItem, deliveryNotes) => {
  return Math.max(0,
    getOrderedForOrderItem(orderItem)
    - getScheduledForOrderItem(orderItem, deliveryNotes)
    - getDeliveredForOrderItem(orderItem, deliveryNotes)
    + getReturnedForOrderItem(user, orderItem, deliveryNotes)
  )
}

export const getDefaultQuantityForOrderItem = (
  user,
  order,
  orderItem,
  deliveryNotes,
  jobCards,
  isReturnNote = false
) => {
  if (isReturnNote)
    return 0

  const defaultQuantity = canCustomiseDeliveryNoteItems(user)
    ? Math.max(
      0,
      getReadyForDeliveryForOrderItem(user, order, null, orderItem, jobCards, deliveryNotes)
    )
    : getOrderedForOrderItem(orderItem)
    - getScheduledForOrderItem(orderItem, deliveryNotes)
    - getDeliveredForOrderItem(orderItem, deliveryNotes)
    + getReturnedForOrderItem(user, orderItem, deliveryNotes)

  if (user.proSync && user.proVersion >= deliveriesSyncVersion && isStockableItem(orderItem)) {
    if (orderItem.stockAllocated < defaultQuantity) return orderItem.stockAllocated
    if (user.settings.pickingMode && orderItem.stockPicked < defaultQuantity) return orderItem.stockPicked
  }

  return defaultQuantity
}

export const getMaximumReturnableForOrderItem = (user, orderItem, deliveryNotes) => {
  return getDeliveredForOrderItem(orderItem, deliveryNotes)
    - getReturnedForOrderItem(user, orderItem, deliveryNotes)
}

export const getDefaultDeliveryMethodId = (deliveryMethods) =>
  get(deliveryMethods.find(deliveryMethod => deliveryMethod.default), 'id')

export const isDeliveringItemsNotReadyForDelivery = (
  user,
  order,
  deliveryNote,
  deliveryNoteItems,
  jobCards,
  deliveryNotes
) => {
  const hasQuantity = (deliveryNoteItem) => deliveryNoteItem.quantity > 0
  const anyItemHasQuantity = deliveryNoteItems.some(hasQuantity)

  if (!anyItemHasQuantity) {
    return true
  }

  const isProduction = isProductionEnabled(user)
  const hasReturnNote = deliveryNote.returnNote
  const isDeliveryNoteItems = deliveryNoteItems.some(deliveryNoteItem =>
    deliveryNoteItem.quantity > 0 &&
    getReadyForDeliveryForOrderItem(
      user,
      order,
      deliveryNote,
      deliveryNoteItem.orderItem,
      jobCards,
      deliveryNotes) === 0 &&
    getDeliveredForOrderItem(deliveryNoteItem.orderItem, deliveryNotes) === 0
  )

  return (isProduction && !hasReturnNote && isDeliveryNoteItems)
}

export const isDeliveringItemNotReadyForDelivery = (
  user,
  order,
  deliveryNote,
  deliveryNoteItem,
  jobCards,
  deliveryNotes
) =>
  isProductionEnabled(user) &&
  deliveryNoteItem.quantity > 0 &&
  getReadyForDeliveryForOrderItem(
    user,
    order,
    deliveryNote,
    deliveryNoteItem.orderItem,
    jobCards,
    deliveryNotes
  ) === 0 &&
  getDeliveredForOrderItem(deliveryNoteItem.orderItem, deliveryNotes) === 0

export const canCustomiseDeliveryNoteItems = (user) =>
  accountHasFeature(user, features.PARTIAL_DELIVERY_NOTES)

export const canAddDeliveryNote = (user, order) =>
  userHasFeature(user, features.DELIVERIES_LICENCE) &&
  userHasPermission(user, permissions.MANAGE_DELIVERY_NOTES) &&
  isConfirmedJob(order)

export const canAddReturnNote = (user, order) => {
  return userHasFeature(user, features.DELIVERIES_LICENCE) &&
    accountHasFeature(user, features.RETURN_NOTES) &&
    userHasPermission(user, permissions.MANAGE_RETURN_NOTES) &&
    isConfirmedJob(order) &&
    hasAnyDelivery(order)
}


export const isPartDeliveredOptionDisabled = (status, user) => {
  const isPartDeliveredOption = status === QuoteStatus.partDelivered
  return isPartDeliveredOption
    ? !accountHasFeature(user, features.PARTIAL_DELIVERY_NOTES)
    : false
}

export const createDeliveryCost = (item, cost) => {
  return {
    ...item,
    desc: 'Delivery Charge',
    partCode: 'DELIVERY',
    unitCost: cost,
    totalCost: cost,
  }
}

export const isProDeliverySchedulingEnabled = (user) => {
  const deliveriesLicences = getProLicencesUsed(user, PRO_LICENCE.Deliveries)
  return !isNil(deliveriesLicences) && deliveriesLicences > 0
}
